import { SyntheticEvent, useCallback, useState } from 'react'
import DialogButton from '../../../Common/Buttons/DialogButton'
import Dialog from '../../../Common/Dialog/Dialog'
import DialogActions from '../../../Common/Dialog/DialogActions'
import DialogContent from '../../../Common/Dialog/DialogContent'
import DialogTitle from '../../../Common/Dialog/DialogTitle'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from '../../../Common/_hooks/useAppDispatch'
import { StoreProductStore } from '../../../UserData/_stores/Store/StoreProductStore'
import { Alert, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material'
import { useAppSelector } from '../../../Common/_hooks/useAppSelector'
import { DeleteStoreProduct } from '../../../UserData/_actions/Store/StoreProductActions'

interface Props {
  storeProduct: StoreProductStore
  opened: boolean
  onClose: () => void
}

export default function DeleteStoreProductDialog(props: Props) {
  const [t] = useTranslation('account-v2')
  const dispatch = useAppDispatch()

  const storeType = useAppSelector(state => state.get('userData').get('stores').get(props.storeProduct.get('id_store'))?.get('slug') ?? '')
  const storeName = useAppSelector(state => state.get('userData').get('stores').get(props.storeProduct.get('id_store'))?.get('name') ?? '')

  const [confirm, setConfirm] = useState(false)

  const resetDialogState = useCallback(() => {
    setConfirm(false)
  }, [])

  const onChangeConfirm = useCallback((e: SyntheticEvent, checked: boolean) => {
    setConfirm(checked)
  }, [])

  const onConfirm = useCallback(() => {
    if (!storeType) {
      return
    }

    dispatch(DeleteStoreProduct(storeType, props.storeProduct.get('id')).set({
      onSuccess: () => props.onClose()
    }))
  }, [storeType, props.storeProduct.get('id'), props.onClose])

  return <Dialog
    open={props.opened}
    maxWidth="xs"
    fullWidth
    onClose={props.onClose}
    TransitionProps={{
      onExited: resetDialogState,
    }}
    showCloseButton
  >
    <DialogTitle>{t('Delete {{name}} from {{store}}', { name: props.storeProduct.get('title'), store: storeName })}</DialogTitle>
    <DialogContent>
      <Stack spacing={2}>
        <Typography variant="body1">{t('Are you sure that you want to permanently delete it from your {{store}} store?', { store: storeName })}</Typography>
        <FormControlLabel
          label={t('I confirm that I want to delete this product from both Art of Where and {{store}}', { store: storeName })}
          control={<Checkbox />}
          checked={confirm}
          onChange={onChangeConfirm}
        />
        <Alert
          severity="info"
        >{t('If you only want to hide it from Art of Where, you can use the "Ignore" feature and use the filters at the top to only show synced products.')}</Alert>
      </Stack>
    </DialogContent>
    <DialogActions>
      <DialogButton
        color="secondary"
        onClick={props.onClose}
      >{t('Cancel')}</DialogButton>
      <DialogButton
        color="error"
        onClick={onConfirm}
        autoFocus
        disabled={!confirm}
      >{t('Delete')}</DialogButton>
    </DialogActions>
  </Dialog>
}
